import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const release = (import.meta.env.VITE_APP_VERSION as string | undefined) || 'dev';
const environment =
  (import.meta.env.VITE_ENV as string | undefined) ||
  (import.meta.env.PROD ? 'production' : 'development');

let initialized = false;

export function initSentry(): void {
  if (initialized || !dsn) return;
  initialized = true;

  Sentry.init({
    dsn,
    release,
    environment,

    // Catch unhandled promise rejections + global errors automatically.
    // Replay is NOT eager — it's fetched from the Sentry CDN on demand
    // below via lazyLoadIntegration. See R2 in
    // docs/BUNDLE-SW-AUDIT-2026-05-16.md.
    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: environment === 'production' ? 0.05 : 0.0,
    replaysOnErrorSampleRate: 1.0,

    // Drop noisy errors that aren't actionable.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],

    beforeSend(event) {
      // Strip query strings that may contain auth tokens.
      if (event.request?.url) {
        try {
          const u = new URL(event.request.url);
          u.search = '';
          event.request.url = u.toString();
        } catch {
          // ignore malformed URL
        }
      }
      return event;
    },
  });

  // Lazy-load the Replay integration from the Sentry CDN. This keeps
  // ~50 KB gz out of the main vendor-sentry chunk; users who never
  // trigger an error (and aren't in the session sample) never download
  // it at all.
  // Docs: https://docs.sentry.io/platforms/javascript/configuration/integrations/replay/#lazy-loading-replay
  Sentry.lazyLoadIntegration('replayIntegration')
    .then((replayIntegration) => {
      Sentry.addIntegration(
        replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      );
    })
    .catch(() => {
      // CDN blocked / offline — Replay simply won't capture. Non-fatal.
    });
}

export const captureException = (err: unknown, ctx?: Record<string, unknown>) =>
  Sentry.captureException(err, ctx ? { extra: ctx } : undefined);

export const setUser = (user: { id?: string; email?: string; role?: string } | null) =>
  Sentry.setUser(user);

export const isSentryEnabled = () => Boolean(dsn);
